"use client"

export type ApiEnvelope<T> = {
  ok: true
  data: T
  request_id: string
}

export type FrontendCachePolicy = "fresh-user" | "stable-metadata"

type ApiErrorEnvelope = {
  ok: false
  error: {
    code: string
    message: string
    details?: unknown
  }
  request_id: string
}

export class ApiError extends Error {
  status: number
  code: string
  details: unknown

  constructor(message: string, status: number, code: string, details: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.code = code
    this.details = details
  }
}

export function apiBaseUrl() {
  return (
    process.env.NEXT_PUBLIC_API_BASE_URL?.replace(/\/+$/, "") ||
    "http://127.0.0.1:8000"
  )
}

async function readJsonResponse<T>(response: Response): Promise<ApiEnvelope<T>> {
  let payload: ApiEnvelope<T> | ApiErrorEnvelope
  try {
    payload = (await response.json()) as ApiEnvelope<T> | ApiErrorEnvelope
  } catch {
    throw new ApiError("Invalid JSON response", response.status, "internal_error", undefined)
  }

  if (!response.ok || payload.ok === false) {
    const error = payload.ok === false ? payload.error : { code: "internal_error", message: "Request failed" }
    throw new ApiError(error.message, response.status, error.code, error.details)
  }

  return payload
}

function buildHeaders(idempotencyKey?: string) {
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
  })
  if (idempotencyKey) {
    headers.set("Idempotency-Key", idempotencyKey)
  }
  return headers
}

function cacheModeForPolicy(policy: FrontendCachePolicy) {
  return policy === "stable-metadata" ? "default" : "no-store"
}

export async function apiGet<T>(
  path: string,
  {
    cachePolicy = "fresh-user",
  }: {
    cachePolicy?: FrontendCachePolicy
  } = {}
) {
  const response = await fetch(`${apiBaseUrl()}${path}`, {
    cache: cacheModeForPolicy(cachePolicy),
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  })
  return readJsonResponse<T>(response)
}

export async function apiDelete<T>(path: string) {
  const response = await fetch(`${apiBaseUrl()}${path}`, {
    cache: "no-store",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
    method: "DELETE",
  })
  return readJsonResponse<T>(response)
}

export async function apiMutate<T>(
  path: string,
  {
    body,
    idempotencyKey,
    method,
  }: {
    body: string
    idempotencyKey: string
    method: "POST" | "PATCH" | "PUT" | "DELETE"
  }
) {
  const response = await fetch(`${apiBaseUrl()}${path}`, {
    cache: "no-store",
    body,
    credentials: "include",
    headers: buildHeaders(idempotencyKey),
    method,
  })
  return readJsonResponse<T>(response)
}
